import type { FC } from 'react';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { PapiPremiumFolderData } from '@wix/da-papi-types';
import { OverlayContextProvider } from '@wix/da-react-context/pkg/OverlayContext';
import { GallectionType } from '../../../../../../types/gallection';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import { Url } from '@wix/da-url';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import GallectionDeviationsTorpedo from '../../GallectionDeviationsTorpedo';
import FolderPaywall from '../../FolderPaywall';
import FolderDescription from '../FolderDescription';
import FolderBreadcrumbs from '../FolderBreadcrumbs/';
import { BiContextProvidedValue, BiData, GalleryPurchaseBiEvent } from '@wix/da-bi/pkg/events';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import AnonPaginationBlocker from '@wix/da-shared-react/pkg/AnonPaginationBlocker';
import { getIsDuperbrowseActive } from '@wix/da-shared-react/pkg/Duperbrowse/redux/selectors';
import InfiniteScrollControls from '../../../_partials/StreamPagination/InfiniteScrollControls';
import useInfiniteFolderDeviations from '../hooks/useInfiniteFolderDeviations';

import s from './FolderInfiniteContent.scss';

export interface Props {
  folderId: number;
  className?: string;
}

const FolderInfiniteContent: FC<Props> = ({ folderId, className }) => {
  const isDuperbrowseActive = useSelector(state => getIsDuperbrowseActive(state as any));
  const {
    currentPage,
    deviations,
    folder,
    subfolder,
    gallectionType,
    hasMore,
    isOwner,
    requestUrl,
    streamId,
  } = useInfiniteFolderDeviations(folderId);
  const { isLoggedIn, contentFilter } = useContext(SessionContext);
  const isAnonGallectionNoPaginationFlagEnabled = useFlag('anon_gallection_no_pagination');
  if (!folder) {
    return null;
  }
  const { pointPrice, isPremiumLocked } = normalizePremiumFolderData(folder.premiumFolderData);
  const isCollection = gallectionType === GallectionType.COLLECTION;
  const shouldShowAnonPaginationBlocker =
    isAnonGallectionNoPaginationFlagEnabled && !isLoggedIn && contentFilter !== 'mature';

  return (
    <OverlayContextProvider
      value={{
        showAuthorTooltip: isCollection,
        showThumbStatusIndicators: false,
      }}
    >
      <BiLoggerContextProvider
        value={{
          section_typeid: gallectionTypeToResourceType(gallectionType),
          sectionid: folder.folderId,
          authorid: folder.owner.useridUuid,
        }}
      >
        <ErrorBoundary debugComponent="FolderInfiniteContent">
          <div className={className}>
            {/* TITLE */}
            {!subfolder && (
              <FolderDescription folder={folder} className={s['folder-description']} />
            )}
            {subfolder && <FolderBreadcrumbs />}

            {/* PAYWALL */}
            {isPremiumLocked && !isOwner && (
              <FolderPaywall
                premiumFolderData={{
                  ...(folder.premiumFolderData as PapiPremiumFolderData),
                  galleryUrl: Url.userGalleryLink({
                    username: folder.owner.username,
                    gallectionType: 'gallery',
                    folderId: folder.folderId,
                    folderName: folder.name,
                  }),
                }}
                folder={folder}
                biData={BiData<GalleryPurchaseBiEvent>({
                  evid: 361,
                  typeid: gallectionTypeToResourceType(gallectionType) ?? 0,
                  itemid: folder.folderId,
                  galleryid: folder.folderId,
                  authorid: folder.owner.useridUuid,
                  points: pointPrice,
                  rank: BiContextProvidedValue,
                  reason_shown: BiContextProvidedValue,
                  section_typeid: BiContextProvidedValue,
                  sectionid: BiContextProvidedValue,
                  sectionname: BiContextProvidedValue,
                  sectionrank: BiContextProvidedValue,
                  sectiontype: BiContextProvidedValue,
                  sort_type: BiContextProvidedValue,
                  widgetname: BiContextProvidedValue,
                })}
              />
            )}

            {/* TORPEDO */}
            {!isPremiumLocked && (deviations.length > 0 || hasMore) && (
              <>
                <GallectionDeviationsTorpedo
                  streamId={streamId}
                  gallectionType={gallectionType}
                  deviations={deviations}
                />
                <InfiniteScrollControls
                  currentPage={currentPage}
                  disableInfiniteScroll={shouldShowAnonPaginationBlocker}
                  isDuperbrowseActive={isDuperbrowseActive}
                  requestUrl={requestUrl}
                  streamId={streamId}
                />
              </>
            )}
            {shouldShowAnonPaginationBlocker && (
              <AnonPaginationBlocker
                className={s['anon-pagination-blocker']}
                joinpoint="gallection-anon-pagination-blocker"
              />
            )}
          </div>
        </ErrorBoundary>
      </BiLoggerContextProvider>
    </OverlayContextProvider>
  );
};
FolderInfiniteContent.displayName = 'FolderInfiniteContent';

export default FolderInfiniteContent;
